/**
 * <rlhf-pairwise> — a head-to-head between two subjects. Renders both artifacts
 * side-by-side (text props, with a `<slot>`-style fallback via the `left`/`right`
 * children when no text is set), lets the operator pick a winner (or a tie) and
 * give a rationale, and appends a `preference.Recorded` event into the SessionStore.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import type { PairwiseChoice, SessionState } from "@platform/rlhf-core"
import { RlhfSessionStoreToken, type RlhfSessionStore } from "./store"
import { RlhfDraftStoreToken, optionalDraftStore, type RlhfDraftStore } from "./drafts"
import { systemClock, type RlhfClock } from "./clock"
import { seamAutoGrow } from "./seam"

type Winner = "left" | "right" | "tie"

export class PairwiseVm implements ViewModel {
  readonly winner: Signal<Winner | null> = signal<Winner | null>(null)
  readonly rationale: Signal<string> = signal("")
  /** True once an unsaved rationale draft was restored from storage (shows the hint). */
  readonly draftRestored: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  private readonly storeState: ReadonlySignal<SessionState>

  constructor(
    private readonly store: RlhfSessionStore,
    readonly leftRef: ReadonlySignal<string>,
    readonly rightRef: ReadonlySignal<string>,
    readonly leftText: ReadonlySignal<string>,
    readonly rightText: ReadonlySignal<string>,
    private readonly actor: ReadonlySignal<string>,
    private readonly clock: RlhfClock,
    private readonly drafts: RlhfDraftStore | null = null,
  ) {
    this.storeState = store.state()
    this.renderDeps = [leftRef, rightRef, leftText, rightText, this.winner, this.rationale, this.draftRestored, this.storeState]
  }

  /** The per-pair key an unsaved rationale draft is stored under. */
  private draftField(): string {
    return `pairwise:${this.leftRef.value}:${this.rightRef.value}:rationale`
  }

  attach(): () => void {
    const existing = this.existing()
    const field = this.draftField()
    if (existing) {
      // A SAVED preference is authoritative — prefill from it and drop any stale draft.
      this.winner.set(existing.winner)
      this.rationale.set(existing.rationale)
      this.drafts?.clear(field)
    } else {
      const draft = this.drafts?.read(field)
      if (draft) {
        this.rationale.set(draft)
        this.draftRestored.set(true)
      }
    }
    return () => {}
  }

  /** The preference already recorded for this exact ordered pair, if any. */
  existing(): PairwiseChoice | undefined {
    const l = this.leftRef.value
    const r = this.rightRef.value
    return this.storeState.value.preferences.find((p) => p.leftRef === l && p.rightRef === r)
  }

  choose(w: Winner): void {
    this.winner.set(w)
  }
  setRationale(text: string): void {
    this.rationale.set(text)
    this.drafts?.write(this.draftField(), text)
  }

  /** Append the preference. No-op without both a winner and a rationale. */
  save(): void {
    const w = this.winner.value
    const r = this.rationale.value.trim()
    if (!w || r.length === 0) return
    this.store.append({
      type: "preference.Recorded",
      at: this.clock.now(),
      actor: this.actor.value,
      choice: { leftRef: this.leftRef.value, rightRef: this.rightRef.value, winner: w, rationale: r },
    })
    // Saved — the durable event supersedes the pre-save draft.
    this.drafts?.clear(this.draftField())
    this.draftRestored.set(false)
  }
}

export class RlhfPairwise extends KitElement<PairwiseVm> {
  readonly #leftRef = signal("")
  readonly #rightRef = signal("")
  readonly #leftText = signal("")
  readonly #rightText = signal("")
  readonly #actor = signal("operator")
  #clock: RlhfClock = systemClock()

  get leftRef(): string {
    return this.#leftRef.value
  }
  set leftRef(v: string) {
    this.#leftRef.set(v)
  }
  get rightRef(): string {
    return this.#rightRef.value
  }
  set rightRef(v: string) {
    this.#rightRef.set(v)
  }
  get leftText(): string {
    return this.#leftText.value
  }
  set leftText(v: string) {
    this.#leftText.set(v)
  }
  get rightText(): string {
    return this.#rightText.value
  }
  set rightText(v: string) {
    this.#rightText.set(v)
  }
  get actor(): string {
    return this.#actor.value
  }
  set actor(v: string) {
    this.#actor.set(v)
  }
  set clock(c: RlhfClock) {
    this.#clock = c
  }

  protected createViewModel(ctx: KitContext): PairwiseVm {
    return new PairwiseVm(
      ctx.inject(RlhfSessionStoreToken),
      this.#leftRef,
      this.#rightRef,
      this.#leftText,
      this.#rightText,
      this.#actor,
      this.#clock,
      optionalDraftStore(ctx),
    )
  }

  /** A letter card that IS the choose affordance — clicking it picks that side. */
  #column(side: "left" | "right", ref: string, text: string, winner: Winner | null): TemplateResult {
    const chosen = winner === side
    return html`<button
      type="button"
      role="radio"
      class=${classMap({ "rlhf-col": true, [`is-${side}`]: true, "is-winner": chosen })}
      data-act="pairwise-choose"
      data-winner=${side}
      aria-checked=${chosen ? "true" : "false"}
      data-ref=${ref}
      aria-label=${`${ref} als besser wählen`}
    >
      <span class="rlhf-col-head" lang="de">
        <span class="rlhf-col-ref">${ref}</span>
        <span class="rlhf-col-pick" aria-hidden="true" lang="de">${chosen ? "✓ gewählt" : "wählen"}</span>
      </span>
      <span class="rlhf-col-body" lang="de">
        ${text
          ? text
              .split(/\n{2,}/)
              .map((p) => p.trim())
              .filter(Boolean)
              .map((p) => html`<span class="rlhf-col-para" lang="de">${p}</span>`)
          : html`<slot name=${side}></slot>`}
      </span>
    </button>`
  }

  protected render(vm: PairwiseVm): TemplateResult {
    const winner = vm.winner.value
    return html`
      <section class="rlhf-pairwise" data-left=${vm.leftRef.value} data-right=${vm.rightRef.value}>
        <div class="rlhf-vs" role="radiogroup" aria-label="Welcher Brief ist besser?">
          ${this.#column("left", vm.leftRef.value, vm.leftText.value, winner)}
          <button
            type="button"
            role="radio"
            class=${classMap({ "rlhf-tie": true, "is-winner": winner === "tie" })}
            data-act="pairwise-choose"
            data-winner="tie"
            aria-checked=${winner === "tie" ? "true" : "false"}
            lang="de"
          >
            Unentschieden
          </button>
          ${this.#column("right", vm.rightRef.value, vm.rightText.value, winner)}
        </div>
        <label class="rlhf-field rlhf-rationale" lang="de">
          <span class="rlhf-field-label" lang="de">Begründung</span>
          <textarea
            class="rlhf-seam"
            rows="1"
            ${seamAutoGrow()}
            placeholder="Warum dieser Brief? Was gibt den Ausschlag?"
            .value=${vm.rationale.value}
            @input=${(e: Event) => vm.setRationale((e.target as HTMLTextAreaElement).value)}
            lang="de"
          ></textarea>
          ${vm.draftRestored.value
            ? html`<span class="rlhf-draft-hint" role="status" lang="de">Entwurf wiederhergestellt</span>`
            : nothing}
        </label>
        <div class="rlhf-pairwise-actions">
          <button type="button" class="rlhf-btn rlhf-btn-primary" data-act="pairwise-save" lang="de">
            Vergleich speichern
          </button>
        </div>
      </section>
    `
  }

  protected override actions(vm: PairwiseVm): ActionMap {
    return {
      "pairwise-choose": ({ winner }) => {
        if (winner === "left" || winner === "right" || winner === "tie") vm.choose(winner)
      },
      "pairwise-save": () => vm.save(),
    }
  }
}
